export default function CoralGablesDestinationsSection() {
  const stats = [
    {
      value: "48%",
      label: "of Coral Gables requests are Latin America or Caribbean business and leisure trips",
    },
    {
      value: "42%",
      label: "of requests are cruise-related — Port of Miami departures with under 3 weeks notice",
    },
    {
      value: "Nov–Apr",
      label: "peak cruise and holiday travel season — passport renewals spike before sailings",
    },
    {
      value: "24-hour",
      label: "shortest emergency window we've accommodated for a Coral Gables business traveler",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "Latin America",
      context: "Colombia, Brazil, Argentina, Chile, Panama, and Peru are primary destinations for Coral Gables business travelers and families with heritage connections. MIA offers direct daily service to Bogota, Medellin, Sao Paulo, Rio, Buenos Aires, Santiago, and Lima. Business trips to Mexico City and Guadalajara are common for finance and trade professionals based along Ponce de Leon Boulevard.",
      urgency: "Business trips to Latin America are often booked with 1–3 weeks of notice — too short for standard processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas & Caribbean",
      context: "The most common leisure destination for Coral Gables families — whether by cruise from the Port of Miami or by direct flight from MIA to Nassau, Jamaica, the Dominican Republic, or the Cayman Islands. Royal Caribbean, Carnival, and Norwegian operate daily Caribbean itineraries that draw significant Coral Gables passenger volume during school breaks and holidays.",
      urgency: "Cruise embarkation deadlines are immovable — passport issues discovered days before sailing require emergency processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Europe",
      context: "London, Paris, Madrid, Rome, Barcelona, and Amsterdam draw Coral Gables travelers for business, cultural tourism, and University of Miami academic programs. Direct MIA service to London, Madrid, and Paris makes European travel more accessible from Coral Gables than from many U.S. cities. Schengen Area entry requires three months of passport validity beyond planned departure.",
      urgency: "UM study-abroad and conference deadlines are fixed — passport delays can cause cancellation of non-refundable program deposits",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Mexico & Central America",
      context: "Cancun, Riviera Maya, Los Cabos, and Costa Rica are staple beach and eco-tourism destinations for Coral Gables families. MIA offers direct service to Cancun and connecting service throughout Mexico and Central America. Business travelers also fly to Mexico City, Monterrey, and Panama City for corporate meetings and trade events.",
      urgency: "Spring break bookings surge in January — passport discoveries spike in February and March before family departures",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Canada",
      context: "Toronto, Montreal, and Vancouver attract Coral Gables business travelers for finance, healthcare, and tech conferences. Canadian ski destinations like Whistler and Banff draw winter sports enthusiasts from the Gables. Canada requires a valid passport for all air arrivals, and many Coral Gables families travel with minors whose five-year passports expire faster than expected.",
      urgency: "Business conference registration deadlines often require confirmed travel 4–6 weeks in advance",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Asia",
      context: "Tokyo, Hong Kong, Singapore, and Seoul draw Coral Gables business travelers in finance, international trade, and technology. Direct MIA service to Tokyo (with connections) and one-stop service throughout Asia makes the Pacific accessible for Coral Gables executives. Visa requirements for China, India, and Vietnam add additional passport complexity and lead time.",
      urgency: "Asian visa processing often requires 2–4 weeks in addition to passport validity — total lead time can exceed 6 weeks",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Coral Gables Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Coral Gables
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Coral Gables travelers head to Latin America, the Caribbean, Europe, and Asia for a mix of business, leisure, cruises, and educational experiences. These are the destinations that create the most passport urgency for our Coral Gables clients.
          </p>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Destination cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal links */}
        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout South Florida</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Miami", href: "/cities/miami/" },
              { name: "West Palm Beach", href: "/cities/west-palm-beach/" },
              { name: "Fort Lauderdale", href: "/cities/fort-lauderdale/" },
              { name: "Boca Raton", href: "/cities/boca-raton/" },
              { name: "Naples", href: "/cities/naples/" },
              { name: "Key West", href: "/cities/key-west/" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}